import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import {
  OperationInitialState,
  OperationObserverService,
  OperationStatus,
} from '@x/common/operation';
import { getFirstFromArray } from '@x/common/utils';
import { AddressService } from '@x/ecommerce-shop/app/core/address/services/address.service';
import { UserPaymentService } from '@x/ecommerce-shop/app/core/auth/services/user-payment.service';
import {
  AuthCreateUserAddress,
  AuthDeleteUserAddress,
  AuthUpdateUserAddress,
} from '@x/ecommerce-shop/app/core/auth/state/auth.actions';
import { AuthState } from '@x/ecommerce-shop/app/core/auth/state/auth.state';
import { RoutingContextService } from '@x/ecommerce-shop/app/core/routing/routing-context.service';
import {
  SubscriberActivate,
  SubscriberPause,
  SubscriberUpdateAddress,
  SubscriberUpdatePaymentMethod,
} from '@x/ecommerce-shop/app/subscriber-checkout/state/subscriber.actions';
import { SubscriberState } from '@x/ecommerce-shop/app/subscriber-checkout/state/subscriber.state';
import {
  IShopAddressInput,
  IShopAuthUser,
  IShopChannelCheckoutDetail,
  IShopPaymentMethod,
  IShopSubscriber,
  IShopUserAddress,
  IShopUserPaymentMethod,
} from '@x/ecommerce/shop-client';
import { Observable, combineLatest, of } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { InvalidRequirement } from './services/subscriber-requirement';
import { SubscriberRequirementService } from './services/subscriber-requirement.service';

@Component({
  selector: 'x-subscriber-checkout-page',
  templateUrl: './subscriber-checkout-page.component.html',
  styleUrls: ['./subscriber-checkout-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-subscriber-checkout-page' },
})
export class SubscriberCheckoutPageComponent {
  @Select(SubscriberState.subscriber) subscriber$: Observable<IShopSubscriber>;
  @Select(SubscriberState.subscriberChannel)
  subscriberChannel$: Observable<IShopChannelCheckoutDetail>;
  @Select(SubscriberState.subscriberChannelRegionIds) channelRegionIds$: Observable<number[]>;
  @Select(AuthState.loggedInUser) user$: Observable<IShopAuthUser>;
  @Select(SubscriberState.subscriptionUrl) subscriptionUrl$: Observable<string>;
  @Select(SubscriberState.subscriberPaymentMethods) paymentMethods$: Observable<
    IShopPaymentMethod[]
  >;

  userAddressRequirements$: Observable<InvalidRequirement> = this.requirementService
    .findRequirementsByCode([
      'requires_physical_address',
      'requires_selected_physical_address',
      'requires_valid_address',
    ])
    .pipe(map(getFirstFromArray<InvalidRequirement>));

  userPaymentMethodRequirements$: Observable<InvalidRequirement> = this.requirementService
    .findRequirementsByCode([
      'requires_payment_method',
      'requires_selected_payment_method',
      'requires_valid_payment_method',
    ])
    .pipe(map(getFirstFromArray<InvalidRequirement>));

  userPaymentMethods$: Observable<IShopUserPaymentMethod[]> = combineLatest([
    this.store.select(AuthState.userActivePaymentMethods),
    this.store.select(SubscriberState.subscriberPaymentMethods),
  ]).pipe(
    map(([userMethods, subscriptionMethods]) => {
      const availMIds = subscriptionMethods.map((m) => m.id);

      return (
        userMethods?.filter((um) => {
          return availMIds.includes(um.method.id);
        }) ?? []
      );
    }),
  );

  userAddressesByCountry$: Observable<IShopUserAddress[]> = combineLatest([
    this.store.select(AuthState.userAddresses),
    this.store.select(SubscriberState.subscriberChannel),
  ]).pipe(
    map(([addresses, channel]) => {
      return (
        addresses.filter((a) => {
          return a.country === channel?.defaultCountry;
        }) ?? []
      );
    }),
  );

  private operationQ$ = this.operationService.createQueue();
  operationQState$ = this.operationQ$.observe().pipe(
    map(({ state }) => state),
    startWith(new OperationInitialState()),
  );

  disableButton$ = this.requirementService.observeRequirements$.pipe(
    map(this.requirementService.requirementsMet),
  );

  constructor(
    private store: Store,
    private addressService: AddressService,
    private operationService: OperationObserverService,
    private requirementService: SubscriberRequirementService,
    private router: Router,
    private route: ActivatedRoute,
    public rc: RoutingContextService,
    private userPaymentService: UserPaymentService,
  ) {}

  addressCreated(address: IShopAddressInput) {
    this.operationQ$.queue(
      this.store
        .dispatch(new AuthCreateUserAddress({ address }))
        .pipe(switchMap(() => this.assignAddress(address))),
    );
  }

  addressSelected(address: IShopUserAddress) {
    this.operationQ$.queue(
      this.assignAddress(this.addressService.getAddressInputFromShopAddress(address)),
    );
  }

  addressUpdated($event: { addressInput: IShopAddressInput; addressId: number }) {
    this.operationQ$.queue(
      this.store
        .dispatch(
          new AuthUpdateUserAddress({ address: $event.addressInput, addressId: $event.addressId }),
        )
        .pipe(switchMap(() => this.assignAddress($event.addressInput))),
    );
  }

  addressDeleted(address: IShopUserAddress) {
    this.operationQ$.queue(
      this.store
        .dispatch(new AuthDeleteUserAddress({ addressId: address.id }))
        .pipe(switchMap(() => this.assignAddress(undefined))),
    );
  }

  setPaymentMethod(userPaymentMethod: IShopUserPaymentMethod) {
    this.operationQ$.queue(
      this.store.dispatch(new SubscriberUpdatePaymentMethod(userPaymentMethod.id)),
    );
  }

  createUserPayment(paymentMethod: IShopPaymentMethod) {
    this.operationQ$.queue(
      this.userPaymentService.createUserPaymentMethod$(paymentMethod).pipe(
        switchMap((userPaymentMethod) => {
          if (!userPaymentMethod || userPaymentMethod.state !== 'ACTIVE') return of('complete');

          return this.store.dispatch(new SubscriberUpdatePaymentMethod(userPaymentMethod.id));
        }),
      ),
    );
  }

  activate(operationStatus: OperationStatus) {
    if (operationStatus === 'loading') return;

    this.operationQ$.queue(
      this.store.dispatch(new SubscriberActivate()).pipe(
        tap(() => {
          this.router.navigate(['success'], {
            relativeTo: this.route,
            queryParamsHandling: 'preserve',
          });
        }),
      ),
    );
  }

  pause(operationStatus: OperationStatus) {
    if (operationStatus === 'loading') return;

    this.operationQ$.queue(
      this.store.dispatch(new SubscriberPause()).pipe(
        tap(() => {
          this.router.navigate([this.rc.redirectUrl]);
        }),
      ),
    );
  }

  private assignAddress(addressInput: IShopAddressInput | undefined) {
    return this.store.dispatch(new SubscriberUpdateAddress(addressInput));
  }
}
